<template>
    <div class="inline-block">
        <div v-if="formOpen === false">
            <template v-if="isEdit">
                <button
                    type="button"
                    @click="formOpen = !formOpen"
                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                        <path
                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                        ></path>
                    </svg>
                </button>
            </template>
            <template v-else>
                <AppButton element="button" type="button" @click="formOpen = !formOpen" :desired-action="desiredAction">
                    <IconPlus class="w-6 h-6" />

                    {{ $tc('messages.dimensions', 1) }}
                </AppButton>
            </template>
        </div>
        <template v-else>
            <Modal :show="formOpen" @close="formOpen = false">
                <form :action="endpoint.url" method="post" @submit.prevent="submit">
                    <div class="md:grid md:grid-cols-12 md:gap-6">
                        <div class="sm:col-span-8">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.title')" for="title" />
                                <FormInput
                                    required
                                    id="title"
                                    v-model="form.title"
                                    name="title"
                                    type="text"
                                    maxlength="180"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.title" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-4">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.numbers', 1)" for="number" />
                                <FormInput
                                    :required="isEdit"
                                    :placeholder="!isEdit ? $t('messages.(optional)') : ''"
                                    id="number"
                                    v-model="form.number"
                                    name="number"
                                    type="number"
                                    step="1"
                                    min="1"
                                    max="9999999"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.number" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.description')" for="description" />
                                <FormTextarea
                                    id="description"
                                    :placeholder="$t('messages.(optional)')"
                                    v-model="form.description"
                                    name="description"
                                    type="text"
                                    maxlength="2000"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.description" class="mt-2" />
                            </div>
                        </div>
                    </div>

                    <div class="flex space-x-2 items-center mt-3">
                        <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                            {{ $t('messages.save') }}
                            <span v-if="form.processing" class="ml-1 btn-spinner" />
                        </AppButton>
                        <AppButton element="button" type="button" @click="cancel()">
                            {{ $t('messages.cancel') }}
                        </AppButton>
                    </div>
                </form>
                <AlertMessage class="mt-5 inline-block" type="info" v-if="info">
                    <template v-slot:title>
                        {{ $t('messages.explanation') }}
                    </template>
                    <template v-slot:body>
                        <p class="whitespace-pre-line">{{ info }}</p>
                    </template>
                </AlertMessage>
            </Modal>
        </template>
    </div>
</template>

<script>
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import AppButton from '@/components/AppButton.vue';
import Modal from '@/components/Modal.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import { IconPlus } from '@tabler/icons-vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    name: 'DimensionForm',
    components: {
        AlertMessage,
        FormTextarea,
        FormLabel,
        FormInput,
        FormError,
        AppButton,
        Modal,
        IconPlus,
    },
    props: {
        endpoint: {
            type: Object,
            required: true,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        desiredAction: {
            type: Boolean,
            default: false,
            required: false,
        },
        dimension: {
            type: Object,
            required: false,
            default: () => ({
                name: '',
                description: '',
                grade_level_ids: [],
            }),
        },
        info: {
            type: String,
            default: '',
            required: false,
        },
    },
    data() {
        return {
            formOpen: false,
            form: this.$inertia.form({
                title: this.dimension.title,
                description: this.dimension.description,
                number: this.dimension.number,
            }),
        };
    },
    methods: {
        cancel() {
            this.formOpen = false;
            this.form.reset('title', 'number', 'description');
            this.form.clearErrors();
        },
        submit() {
            this.form.submit(this.endpoint.method, this.endpoint.url, {
                preserveScroll: true,
                onSuccess: () => {
                    this.form.reset('title', 'number', 'description');
                    this.formOpen = false;
                },
            });
        },
    },
};
</script>
